import { useTranslation } from "react-i18next";
import { Grid2, Stack, useTheme } from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { InvestigationCollapsable, Separator } from "../components";
import {
  ACCOUNT_FLOWS,
  formatAmountWithCurrency,
  getAccountsTotal,
  getNetLoss,
  normalizeFinancialImpact,
} from "../../../../../helpers/financialImpact";
import { AccountDetails, AccountTitle } from "./AccountSummary";
import { AmountRow, CollapsablePanel, useFlowColor } from "./components";

export const SingleFinancialImpact = ({
  financialImpact,
  setIsEditing,
  justView,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const flowColor = useFlowColor();

  const impact = normalizeFinancialImpact(financialImpact);
  const { currency, accounts } = impact;

  const actions = [
    {
      name: t("speeddial.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.secondary.main} />,
      onClick: () => setIsEditing(true),
    },
  ];

  return (
    <EGAccordionSingle
      title={t("financialImpact.single_title")}
      bgColor={"#7BBDFF"}
      color={theme.palette.white.main}
      actions={!justView ? actions : undefined}
    >
      <Grid2 container spacing={2}>
        <InvestigationBlockInfo
          label={t("financialImpact.currency")}
          text={currency}
          size={{ xs: 12, sm: 6, lg: 3 }}
        />
        <InvestigationBlockInfo
          label={t("financialImpact.potential_amount")}
          text={formatAmountWithCurrency(impact.potential_amount, currency)}
          size={{ xs: 12, sm: 6, lg: 3 }}
        />
        <InvestigationBlockInfo
          label={t("financialImpact.final_amount")}
          text={formatAmountWithCurrency(impact.final_amount, currency)}
          size={{ xs: 12, sm: 6, lg: 3 }}
        />
        <InvestigationBlockInfo
          label={t("financialImpact.recovered_amount")}
          text={formatAmountWithCurrency(impact.recovered_amount, currency)}
          size={{ xs: 12, sm: 6, lg: 3 }}
        />
        <Grid2 size={12}>
          <AmountRow
            label={t("financialImpact.net_loss")}
            amount={getNetLoss(impact)}
            currency={currency}
            color={theme.palette.red.main}
          />
        </Grid2>
      </Grid2>

      {(impact.origin || impact.destination) && (
        <>
          <Separator text={t("financialImpact.money_trail")} sx={{ my: 2 }} />
          <Grid2 container spacing={2}>
            <InvestigationBlockInfo
              label={t("financialImpact.origin")}
              text={impact.origin}
              size={{ xs: 12, lg: 6 }}
            />
            <InvestigationBlockInfo
              label={t("financialImpact.destination")}
              text={impact.destination}
              size={{ xs: 12, lg: 6 }}
            />
          </Grid2>
        </>
      )}

      {accounts.length > 0 && (
        <Stack spacing={2} mt={2}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <AmountRow
                label={t("financialImpact.affected_total")}
                amount={getAccountsTotal(accounts, ACCOUNT_FLOWS.AFFECTED)}
                currency={currency}
                color={flowColor(ACCOUNT_FLOWS.AFFECTED)}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <AmountRow
                label={t("financialImpact.beneficiary_total")}
                amount={getAccountsTotal(accounts, ACCOUNT_FLOWS.BENEFICIARY)}
                currency={currency}
                color={flowColor(ACCOUNT_FLOWS.BENEFICIARY)}
              />
            </Grid2>
          </Grid2>

          {/* Cerrado por defecto: el detalle de cuentas es dato sensible y
              largo, y no tiene que abrirse solo cada vez que se entra. */}
          <CollapsablePanel
            icon="bank"
            title={t("financialImpact.accounts")}
            badge={`${accounts.length}`}
          >
            <Stack spacing={1}>
              {accounts.map((account, idx) => (
                <InvestigationCollapsable
                  key={`account-${idx}`}
                  title={<AccountTitle account={account} currency={currency} />}
                  details={<AccountDetails account={account} />}
                />
              ))}
            </Stack>
          </CollapsablePanel>
        </Stack>
      )}

      {impact.notes && (
        <Grid2 container spacing={2} mt={2}>
          <InvestigationBlockInfo
            label={t("financialImpact.notes")}
            text={impact.notes}
            size={12}
          />
        </Grid2>
      )}
    </EGAccordionSingle>
  );
};
